/* Moving a block by dragging it.
 *
 * Until now the only way to move something was a dialog: open it, type a time,
 * confirm. That is fine for "Thursday at four" and terrible for the thing
 * people actually do, which is look at a day and slide something an hour later
 * because the morning went wrong.
 *
 * One hook, used by both the day and the week, because the two differ only in
 * geometry. Everything else — pointer capture, the threshold before a drag
 * starts, the snap, the preview, the refusal — is identical, and two copies of
 * it would drift within a week.
 *
 * Four decisions worth stating:
 *
 *   · **Pointer events, not the HTML5 drag API.** Drag-and-drop is unusable on
 *     touch, will not let you draw a live preview, and cannot be cancelled with
 *     Escape. A pointer is a pointer on every device.
 *
 *   · **A threshold before it counts as a drag.** Six pixels. Without one,
 *     every click on a block is a one-pixel move that rewrites the day, and the
 *     buttons inside a block stop working.
 *
 *   · **Drop targets describe themselves.** A container carries
 *     `data-drop-date`, `data-top-min` and `data-span-min`, and the hook reads
 *     the time out of the geometry. That is what lets the same code drop onto a
 *     week column and onto a day rail without knowing which it is.
 *
 *   · **The drop is a request, not a fact.** It goes through the same PATCH the
 *     dialog uses, so sleep is still protected and a clash is still refused by
 *     name. The block springs back and the refusal is spoken; nothing is
 *     written by moving a finger.
 */

import { useCallback, useRef, useState } from 'react';

/** Snap, in minutes. Fifteen is what a calendar means by "quarter past". */
const SNAP = 15;
const THRESHOLD = 6;

export interface DropAt {
  date: string;
  startMin: number;
}

export interface DragState {
  id: string;
  /** Where the pointer is now, for drawing the preview. */
  x: number;
  y: number;
  /** The drop that would happen if the pointer were released here, or null
      when it is over nothing droppable. */
  at: DropAt | null;
  /** Resizing the end rather than moving the whole thing. */
  resizing: boolean;
  /** Where the block being dragged starts now — the anchor a resize measures
      against, since the pointer is giving the new *end*. */
  fromMin: number;
}

/** Read a time out of whatever is under the pointer. */
function resolve(x: number, y: number): DropAt | null {
  const el = document.elementFromPoint(x, y);
  const target = el?.closest<HTMLElement>('[data-drop-date]');
  if (!target) return null;

  const date = target.dataset.dropDate!;
  const topMin = Number(target.dataset.topMin ?? 0);
  const spanMin = Number(target.dataset.spanMin ?? 1440);
  const box = target.getBoundingClientRect();
  if (box.height <= 0) return null;

  const share = Math.min(1, Math.max(0, (y - box.top) / box.height));
  const raw = topMin + share * spanMin;
  return { date, startMin: Math.max(0, Math.round(raw / SNAP) * SNAP) };
}

export function useBlockDrag({ onMove, onResize }: {
  onMove: (id: string, at: DropAt) => void;
  onResize?: (id: string, minutes: number) => void;
}) {
  const [drag, setDrag] = useState<DragState | null>(null);
  const armed = useRef<
    { id: string; x: number; y: number; fromMin: number; resizing: boolean } | null>(null);

  /**
   * @param fromMin where the block currently starts. A move does not need it;
   *   a resize does, because the pointer is giving the new end and the length
   *   is the difference.
   */
  const start = useCallback((
    e: React.PointerEvent,
    id: string,
    fromMin: number,
    resizing = false
  ) => {
    /* Only the primary button, and never on a control inside the block: a
       drag that starts on "Done" is a Done that never fires. */
    if (e.button !== 0) return;
    if ((e.target as HTMLElement).closest('button, a, input, select')) {
      if (!resizing) return;
    }
    armed.current = { id, x: e.clientX, y: e.clientY, fromMin, resizing };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  }, []);

  const move = useCallback((e: React.PointerEvent) => {
    const a = armed.current;
    if (!a) return;

    if (!drag) {
      const far = Math.abs(e.clientX - a.x) + Math.abs(e.clientY - a.y);
      if (far < THRESHOLD) return;              // still a click
    }

    setDrag({
      id: a.id, x: e.clientX, y: e.clientY,
      at: resolve(e.clientX, e.clientY),
      resizing: a.resizing, fromMin: a.fromMin
    });
  }, [drag, onResize]);

  const end = useCallback((e: React.PointerEvent) => {
    const a = armed.current;
    armed.current = null;
    if (!a || !drag) { setDrag(null); return; }

    const at = resolve(e.clientX, e.clientY);
    setDrag(null);
    if (!at) return;                            // dropped on nothing: springs back

    if (a.resizing) {
      /* The pointer gave the new END; the start stays where it was, so the
         length is the difference. Never shorter than one snap — below that it
         is a mis-drag rather than an intention. */
      onResize?.(a.id, Math.max(SNAP, at.startMin - a.fromMin));
      return;
    }
    onMove(a.id, at);
  }, [drag, onMove, onResize]);

  const cancel = useCallback(() => { armed.current = null; setDrag(null); }, []);

  return { drag, start, move, end, cancel, SNAP };
}
